import { endOfMonth, startOfMonth, sub } from "date-fns";
import { uniqBy } from "lodash";
import isNil from "lodash/isNil";
import queryString from "query-string";
import { useCallback, useContext, useMemo } from "react";
import { useMutation, useQueries, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import {
  deleteProperty,
  getProperty,
  getPropertyDisclaimers,
  getPropertyIncides,
  getPropertyPriceHistory,
  postProperty,
  putProperty,
  searchProperty,
} from "../api-services/property";
import {
  ACCOUNT_CATEGORY,
  EVENT_TRACKING,
  STATE_DISCLAIMER_MAPPING,
} from "../commons/enum";
import { processingEstimateHistory } from "../components/Layout/get-property";
import JointAccountContext from "../context/joint-account-context";
import {
  calculatePropertyDebtList,
  calculatePropertyEstimateHistory,
  calculateTotalPropertyDebtData,
} from "../utils/calculate-property-data-util";
import { getByPathOrDefault } from "../utils/expensesUtils";
import { trackingGoogleTagEvent } from "../utils/google-tag-manager-tracking";
import { by } from "../utils/object-util";
import { useBalanceHistories, useLinkedBankAccountData } from "./account.hooks";
import {
  useSelectedJointAccountAggregationData,
  useSelectedJointAccountPropertiesPriceHistory,
} from "./joint-account.hooks";
import { useTransactions } from "./transaction.hooks";
import { useIsLoggedIn, useMyInfo } from "./user.hooks.ts";
import { useQueryInvalidationWithNotification } from "./util.hooks";

export const useIndividualPropertyList = () => {
  const isLoggedIn = useIsLoggedIn();
  return useQuery(["propertyList"], getProperty, {
    enabled: isLoggedIn,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const usePropertyList = () => {
  const { selectedJointAccount } = useContext(JointAccountContext);
  const { data: jointAccountData } =
    useSelectedJointAccountAggregationData(selectedJointAccount);
  const individualPropertyList = useIndividualPropertyList();
  if (selectedJointAccount) {
    return { data: jointAccountData?.properties };
  }
  return individualPropertyList;
};

export const useIndividualPropertyPriceHistory = () => {
  const { selectedJointAccount } = useContext(JointAccountContext);
  const { data: propertyList } = usePropertyList();
  const queryKey = ["propertyPriceHistory", propertyList];
  return useQuery(queryKey, getPropertyPriceHistory, {
    enabled:
      !selectedJointAccount && !!(propertyList && propertyList.length > 0),
  });
};

export const usePropertyPriceHistory = () => {
  const { selectedJointAccount } = useContext(JointAccountContext);
  const { data: jointAccountData } =
    useSelectedJointAccountPropertiesPriceHistory(selectedJointAccount);
  const individualPropertyList = useIndividualPropertyPriceHistory();
  if (selectedJointAccount) {
    return { data: jointAccountData };
  }
  return individualPropertyList;
};

export const useDetailPropertyList = () => {
  const { data: propertyList } = usePropertyList();
  const { data: propertyPriceHistory } = usePropertyPriceHistory();

  return useMemo(() => {
    let detailPropertyList = propertyList;

    if (propertyPriceHistory) {
      detailPropertyList = processingEstimateHistory(
        detailPropertyList,
        propertyPriceHistory
      );
    }

    return detailPropertyList;
  }, [propertyList, propertyPriceHistory]);
};

export const useMarketIndices = () => {
  const { data: propertyList } = usePropertyList();
  const propertyIndicesParams = useMemo(
    () =>
      (propertyList || []).map((item) => {
        const { addressDetails = {}, propertyType = "" } = item;
        const { locality = "", postcode = "", state = "" } = addressDetails;
        return {
          interval: "years",
          totalPeriods: 10,
          startingPeriodRelativeToCurrent: 1,
          propertyType,
          stats: ["price"],
          state,
          suburb: locality,
          postcode,
        };
      }),
    [propertyList]
  );

  const uniquePropertyIndicesParams = useMemo(
    () =>
      uniqBy(propertyIndicesParams, (params) => queryString.stringify(params)),
    [propertyIndicesParams]
  );

  const queries = useQueries(
    uniquePropertyIndicesParams.map((params) => ({
      queryKey: ["marketIndices", params],
      queryFn: () => getPropertyIncides({ params }),
      enabled: !!(propertyList && propertyList.length > 0),
    }))
  );

  const getQueryByPropertyDetail = useCallback(
    ({ propertyType, state, locality, postcode }) => {
      const paramsIndex = uniquePropertyIndicesParams.findIndex(
        (params) =>
          params.propertyType === propertyType &&
          params.state === state &&
          params.suburb === locality &&
          params.postcode === postcode
      );
      if (paramsIndex < 0) {
        return undefined;
      }
      return queries[paramsIndex];
    },
    [uniquePropertyIndicesParams, queries]
  );

  return {
    getQueryByPropertyDetail,
    queries,
    paramsArr: uniquePropertyIndicesParams,
  };
};

export const useTotalMarketIndices = () => {
  const { queries, paramsArr } = useMarketIndices();
  const isLoading = queries.some((query) => query.isLoading);
  const dates = useMemo(() => {
    return [...new Set(queries.map(({ data = {} }) => data.dates).flat())].sort(
      (a, b) => new Date(a) - new Date(b)
    );
  }, [queries]);
  const avgPriceByPeriod = useMemo(() => {
    return dates.map((date) => {
      const totalPrice = queries.reduce(
        (acc, { data }, index) => {
          const dateIndex = (data?.dates || []).findIndex(
            (_date) => _date === date
          );
          if (dateIndex < 0) {
            return acc;
          }
          const { propertyType, state, suburb, postcode } = paramsArr[index];
          const priceArr = getByPathOrDefault(
            data,
            [
              propertyType.toLowerCase(),
              "series",
              `${state}-${suburb}-${postcode}`,
              "price",
            ],
            []
          );

          return {
            price: acc.price + (priceArr?.[dateIndex] || 0),
            nonNullPriceItem:
              acc.nonNullPriceItem + Number(priceArr?.[dateIndex] != null),
          };
        },
        {
          price: 0,
          nonNullPriceItem: 0,
        }
      );
      return {
        x: date,
        y:
          totalPrice.nonNullPriceItem > 0
            ? totalPrice.price / totalPrice.nonNullPriceItem
            : 0,
      };
    });
  }, [dates, paramsArr, queries]);

  if (isLoading) {
    return [];
  }

  return avgPriceByPeriod;
};

export const usePropertyDebtList = () => {
  const propertyList = useDetailPropertyList();
  const categorizedAccounts = useLinkedBankAccountData();
  const propertyLoanAccounts = useMemo(() => {
    return [
      ...(categorizedAccounts?.[ACCOUNT_CATEGORY.PROPERTY_LOAN] || []),
      ...(categorizedAccounts?.[ACCOUNT_CATEGORY.PERSONAL_LOAN] || []),
      ...(categorizedAccounts?.[ACCOUNT_CATEGORY.LOAN] || []),
    ];
  }, [categorizedAccounts]);
  const propertyLoanIds = useMemo(() => {
    return (
      [...new Set(propertyLoanAccounts)]?.map(({ accountId }) => accountId) ||
      []
    );
  }, [propertyLoanAccounts]);
  const toDate = new Date();
  const fromDate = sub(toDate, { years: 15 });
  const balanceHistoryQueries = useBalanceHistories({
    accountIds: propertyLoanIds,
    from: fromDate,
    to: toDate,
  });
  const balanceHistories = useMemo(
    () =>
      balanceHistoryQueries?.filter(({ data }) => !isNil(data)).map(by("data")),
    [balanceHistoryQueries]
  );

  // * 3 month repayment txs
  const startLastMonthDate = startOfMonth(sub(toDate, { months: 1 }));
  const endLastMonthDate = endOfMonth(startLastMonthDate);
  const propertyLoanPaymentTransactionQueries = useTransactions({
    accountIds: propertyLoanIds,
    direction: "credit",
    from: startLastMonthDate,
    to: endLastMonthDate,
    txClass: "loan-repayment",
  });
  const propertyLoanPaymentTransactions = useMemo(
    () =>
      propertyLoanPaymentTransactionQueries
        ?.filter(({ data }) => !isNil(data))
        .map(by("data")),
    [propertyLoanPaymentTransactionQueries]
  );

  return useMemo(
    () =>
      propertyList?.length > 0
        ? calculatePropertyDebtList(
            propertyList,
            propertyLoanAccounts,
            balanceHistories,
            propertyLoanPaymentTransactions
          )
        : [],
    [
      propertyList,
      propertyLoanAccounts,
      balanceHistories,
      propertyLoanPaymentTransactions,
    ]
  );
};

export const useAddPropertyMutation = () => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  const { enqueueSnackbar } = useSnackbar();
  const { data: userInfo } = useMyInfo();
  return useMutation(
    (data) => {
      return postProperty(data);
    },
    {
      onSuccess: (property) => {
        const {
          addressDetails: { address, locality, state, postcode },
          estimate,
        } = property;
        const propertyConversion = {
          property: {
            street_address: address,
            suburb: locality,
            city: state,
            zip: postcode,
            valuation: estimate,
          },
        };
        trackingGoogleTagEvent(
          EVENT_TRACKING.PROPERTY_VALUATION,
          userInfo,
          propertyConversion
        );
        invalidateQueries("propertyList");
      },
      onError: (error) => {
        const {
          data: { message = "Something went wrong, please try again" } = {},
        } = error;
        enqueueSnackbar(message, { variant: "error" });
      },
    }
  );
};

export const useUpdatePropertyMutation = () => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useMutation(
    (data) => {
      const { id, params } = data;
      return putProperty(params, id);
    },
    {
      onSuccess: () => {
        invalidateQueries("propertyList");
      },
    }
  );
};
export const useDeletePropertyMutation = () => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useMutation(deleteProperty, {
    onSuccess: () => {
      invalidateQueries("propertyList");
    },
  });
};

export const useSearchAddressMutation = () => {
  return useMutation((data) => {
    return searchProperty(data);
  });
};

export const useTotalPropertyDebtData = () => {
  const propertyDebtList = usePropertyDebtList();
  return useMemo(
    () => calculateTotalPropertyDebtData(propertyDebtList),
    [propertyDebtList]
  );
};

export const useTotalPropertyDetail = () => {
  const propertyList = useDetailPropertyList();
  return useMemo(
    () => calculatePropertyEstimateHistory(propertyList),
    [propertyList]
  );
};

export const useDisclaimer = (state) => {
  const disclaimerIds = [STATE_DISCLAIMER_MAPPING.APM]; // Always include APM
  if (state && STATE_DISCLAIMER_MAPPING[state]) {
    disclaimerIds.push(STATE_DISCLAIMER_MAPPING[state]);
  }

  return useQuery(["getDisclaimer", disclaimerIds.join(",")], () =>
    getPropertyDisclaimers({
      ids: disclaimerIds.join(","),
    })
  );
};
